// NPCs: world-placed characters built on the same avatar pipeline as player
// avatars (image → Meshy → Tripo rig, functions/api/tripo/avatar.ts), then
// looped on one move from the shared Tripo animation library.
import tripoLibrary from '../avatar/tripoLibrary.json';
import type { NpcVoiceTier } from './npcVoices';

export type NpcGender = 'Female' | 'Male';
export type NpcEthnicity = 'African American' | 'Caucasian' | 'Asian' | 'Latin';
export type NpcStatus = 'building' | 'ready' | 'failed';

/** Conversation settings (functions/api/npc/talk.ts). Paid by the world owner in talk credits. */
export interface NpcTalk {
  enabled: boolean;
  /** Who the NPC is: role, personality, what it knows about this world. */
  persona: string;
  /** Said when a visitor first walks up. Optional. */
  greeting?: string;
  tier: NpcVoiceTier;
  /** Id from LEMONFOX_VOICES or ELEVENLABS_VOICES (src/config/npcVoices.ts). */
  voice: string;
}
export const PERSONA_MAX = 800;
export const GREETING_MAX = 160;

export interface NpcConfig {
  name: string;
  gender: NpcGender;
  ethnicity: NpcEthnicity;
  type: string;
  clothing: string;
  style: string;
  /** NPC_ANIMS id: a library preset name, or 'wander' / 'jog' for moving around. */
  anim: string;
  /** Metres from the placed spot an NPC may walk to (wander / jog only). */
  radius: number;
  status: NpcStatus;
  avatarUrl?: string;
  avatarId?: string;
  thumbnailUrl?: string;
  buildId?: string;
  error?: string;
  talk?: NpcTalk;
}

export const NPC_GENDERS: NpcGender[] = ['Female', 'Male'];
export const NPC_ETHNICITIES: NpcEthnicity[] = ['African American', 'Caucasian', 'Asian', 'Latin'];
export const NPC_STYLES = ['Realistic', 'Stylized', 'Cartoon', 'Anime'];

/** Character types, each with the outfits that fit it. */
export const NPC_TYPES: { id: string; outfits: string[] }[] = [
  { id: 'Casual', outfits: ['T-shirt and jeans', 'hoodie and joggers', 'sweater and chinos', 'denim jacket and jeans', 'sundress and sandals'] },
  { id: 'Business', outfits: ['tailored business suit', 'blazer and slacks', 'pencil skirt and blouse', 'button-down shirt and trousers'] },
  { id: 'Streetwear', outfits: ['oversized hoodie and cargo pants', 'bomber jacket and sneakers', 'graphic tee and track pants', 'puffer jacket and jeans'] },
  { id: 'Party', outfits: ['cocktail dress', 'sequin top and leather pants', 'velvet blazer and black jeans', 'silk shirt and tailored trousers'] },
  { id: 'Athletic', outfits: ['tracksuit', 'basketball jersey and shorts', 'running gear', 'yoga outfit'] },
  { id: 'Service', outfits: ['chef uniform', 'medical scrubs', 'bartender vest and bow tie', 'security guard uniform', 'barista apron over a T-shirt'] },
  { id: 'Fantasy', outfits: ['knight armour', 'wizard robes', 'elven ranger outfit', 'pirate captain coat'] },
  { id: 'Sci-Fi', outfits: ['futuristic jumpsuit', 'cyberpunk jacket with neon trim', 'space explorer suit', 'sleek android-style bodysuit'] },
];
export const RANDOM = 'Random';

export interface NpcAnim { id: string; label: string; group: string; moves?: boolean }
const has = (n: string) => n in (tripoLibrary.clips as Record<string, unknown>);
export const NPC_ANIMS: NpcAnim[] = ([
  { id: 'idle', label: 'Standing', group: 'Standing' },
  { id: 'standing_relax', label: 'Relaxed', group: 'Standing' },
  { id: 'look_around', label: 'Looking around', group: 'Standing' },
  { id: 'wait', label: 'Waiting', group: 'Standing' },
  { id: 'fold_arms', label: 'Arms folded', group: 'Standing' },
  { id: 'wander', label: 'Walk around', group: 'Moving', moves: true },
  { id: 'jog', label: 'Jog around', group: 'Moving', moves: true },
  { id: 'dance_01', label: 'Dance 1', group: 'Dancing' },
  { id: 'dance_02', label: 'Dance 2', group: 'Dancing' },
  { id: 'dance_03', label: 'Dance 3', group: 'Dancing' },
  { id: 'dance_04', label: 'Dance 4', group: 'Dancing' },
  { id: 'dance_05', label: 'Dance 5', group: 'Dancing' },
  { id: 'dance_06', label: 'Dance 6', group: 'Dancing' },
  { id: 'freaky', label: 'Freestyle', group: 'Dancing' },
  { id: 'greet_01', label: 'Greeting', group: 'Social' },
  { id: 'wave_goodbye_01', label: 'Waving', group: 'Social' },
  { id: 'clap', label: 'Clapping', group: 'Social' },
  { id: 'cheer', label: 'Cheering', group: 'Social' },
  { id: 'laugh_01', label: 'Laughing', group: 'Social' },
  { id: 'sing_01', label: 'Singing', group: 'Social' },
  { id: 'make_a_call_01', label: 'On the phone', group: 'Busy' },
  { id: 'play_mobile_game', label: 'On their phone', group: 'Busy' },
  { id: 'play_video_game', label: 'Gaming', group: 'Busy' },
  { id: 'warm_up', label: 'Warming up', group: 'Busy' },
  { id: 'dribble', label: 'Dribbling', group: 'Busy' },
  { id: 'box_01', label: 'Boxing', group: 'Busy' },
] as NpcAnim[]).filter((a) => a.moves || has(a.id));

export const npcAnim = (id: string) => NPC_ANIMS.find((a) => a.id === id) ?? NPC_ANIMS[0];
export const clipUrl = (preset: string): string =>
  (tripoLibrary.clips as Record<string, { url: string }>)[preset]?.url ?? (tripoLibrary.clips as any).idle.url;

const FIRST: Record<NpcGender, Record<NpcEthnicity, string[]>> = {
  Female: {
    'African American': ['Aaliyah', 'Imani', 'Keisha', 'Nia', 'Zora', 'Ebony', 'Jada', 'Amara', 'Tiana', 'Monique'],
    Caucasian: ['Claire', 'Emma', 'Hannah', 'Sophie', 'Molly', 'Grace', 'Abby', 'Kate', 'Lucy', 'Olivia'],
    Asian: ['Mei', 'Yuki', 'Hana', 'Ji-woo', 'Lin', 'Sakura', 'Mai', 'Priya', 'Anh', 'Soo-ah'],
    Latin: ['Sofia', 'Valentina', 'Camila', 'Lucia', 'Isabella', 'Ximena', 'Mariana', 'Daniela', 'Paola', 'Rosa'],
  },
  Male: {
    'African American': ['Marcus', 'Jamal', 'Darnell', 'Andre', 'Malik', 'Terrence', 'Isaiah', 'Xavier', 'Corey', 'Dante'],
    Caucasian: ['Jake', 'Ethan', 'Connor', 'Ryan', 'Owen', 'Liam', 'Tyler', 'Max', 'Ben', 'Sam'],
    Asian: ['Kenji', 'Wei', 'Hiro', 'Min-jun', 'Ravi', 'Tao', 'Jin', 'Arjun', 'Kai', 'Duc'],
    Latin: ['Diego', 'Mateo', 'Carlos', 'Javier', 'Luis', 'Santiago', 'Rafael', 'Andres', 'Miguel', 'Alejandro'],
  },
};
const pick = <T,>(xs: T[]): T => xs[Math.floor(Math.random() * xs.length)];
export const randomName = (g: NpcGender, e: NpcEthnicity) => pick(FIRST[g][e]);
export const randomOf = pick;

const LOOK: Record<NpcEthnicity, string> = {
  'African American': 'African American, rich brown to deep brown skin',
  Caucasian: 'Caucasian, fair to light skin',
  Asian: 'East or South Asian',
  Latin: 'Latino, warm tan to olive skin',
};

/**
 * Full-body A-pose reference prompt for the image step of the avatar build.
 * A-pose, not T-pose: in a T-pose picture loose tops and sleeves fuse the arms
 * to the torso, and the rig then stretches them into sheets once the arms come
 * down. A gap between arms and body keeps them separate; the retarget aligns
 * the limbs to the library's T-pose rest (libraryRetarget.ts).
 */
export function npcPrompt(c: Pick<NpcConfig, 'gender' | 'ethnicity' | 'type' | 'clothing' | 'style'>, outfit: string, extra = ''): string {
  const person = c.gender === 'Female' ? 'woman' : 'man';
  return [
    'Generate a full-body character standing in a relaxed A-pose (arms straight and angled about 45 degrees down from the shoulders, a clear gap between each arm and the body, hands open with palms facing the thighs, legs slightly apart, facing forward)',
    `${c.style.toLowerCase()} 3D game character style`,
    `an adult ${LOOK[c.ethnicity]} ${person}`,
    `${c.type.toLowerCase()} look, wearing ${outfit}`,
    'natural hairstyle that suits the outfit, closed-toe shoes',
    'clothing fitted at the arms and armpits, nothing draping between the arms and the body',
    extra.trim(),
    'sharp, well-lit face, centered on a plain light-gray background, full body visible from head to feet, single character, no text',
  ].filter(Boolean).join(', ');
}
